"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface ProblemPillar {
  title: string;
  body: string;
  icon: LucideIcon;
  accent: string;
}

export interface ProblemSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  lede?: string;
  pillars: ProblemPillar[];
  accent: string;
}

export function ProblemSection({
  id = "problem",
  eyebrow,
  title,
  lede,
  pillars,
  accent,
}: ProblemSectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/3 right-1/4 h-72 w-72 rounded-full opacity-15 blur-[120px]"
          style={{ background: accent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            {lede && (
              <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{lede}</p>
            )}
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 80}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${p.accent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="grid place-items-center h-11 w-11 rounded-xl"
                      style={{
                        background: `color-mix(in oklch, ${p.accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${p.accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: p.accent }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: p.accent }}>
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="text-lg">{p.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
